<script setup>
    import { ref, onMounted, onUnmounted, onBeforeUnmount, computed, onUpdated, watch } from 'vue'
    import { storeToRefs } from 'pinia'
    import { reflectPreference, onClick } from '@/stores/helpers/themeToggle'
    import { vOnClickOutside } from '@vueuse/components'
    import  userAvatar from '@/components/userAvatar.vue'
    
    import Notification from '@/components/Notification.vue'
    import { useNotificationStore } from '@/stores/notification';
    const notificationStore = useNotificationStore();
    const { notifications } = storeToRefs(notificationStore);

    const removeNotification = (id) => {
        const identifier = notificationStore.removeNotification(id);
        if (identifier === 'accessDenied') {
            authStore.resetAccessDenied();
        }
    };
    
    import router from '../router/routes';

    import { useIncentivesStore } from '@/stores/incentives'
    const incentivesStore = useIncentivesStore();

    import { useRatingsStore } from '@/stores/ratings';
    const ratingsStore = useRatingsStore();

    import { useBusinessPartnersStore } from '@/stores/businessPartners'
    const businessPartnersStore = useBusinessPartnersStore();

    import { useBusinessPartnersCountStore } from '@/stores/businessPartnersCount'
    const businessPartnersCountStore = useBusinessPartnersCountStore();
    
    import { useUserStore } from '@/stores/user'
    const userStore = useUserStore();

    import { usePerformanceStore } from '@/stores/performance'
    const performanceStore = usePerformanceStore();

    import { useFunctionsStore } from '@/stores/functions'
    const functionsStore = useFunctionsStore()

    import { useAdminStore } from '@/stores/admin'
    import { useChangelogStore } from '@/stores/changelog'

    // ProgressBar für Kampagnen-Ladezeit
    import ProgressBar from '@/components/ProgressBar.vue'
    import { useCampaignLoadingStore } from '@/stores/campaignLoading'
    import { useRoute } from 'vue-router'
    const campaignLoadingStore = useCampaignLoadingStore();
    const route = useRoute();
    const adminStore = useAdminStore();
    const changelogStore = useChangelogStore();

    const avatarMenuInfo = computed(() => {
        const all = Array.isArray(userStore.colleagues) ? userStore.colleagues : (Array.isArray(adminStore?.colleagues) ? adminStore.colleagues : []);
        let colleague = all.find(c => Number(c.id) === Number(userStore.id));
        if (!colleague && Array.isArray(adminStore?.colleagues)) colleague = adminStore.colleagues.find(c => Number(c.id) === Number(userStore.id));
        const name = colleague ? `${colleague.firstName || ''} ${colleague.lastName || ''}`.trim() : `${userStore.firstName || ''} ${userStore.lastName || ''}`.trim();
        const status = colleague?.isSalesAgent?.salesAgentStatusCurrent || colleague?.isSalesAgent?.salesAgentStatusPlanned || null;
        let companyName = null;
        const salesTeams = [];
        const salesTeamManagers = [];
        try {
            const roles = Array.isArray(colleague?.roles) ? colleague.roles : [];
            for (const r of roles) {
                if (Array.isArray(r.salesTeams)) {
                    for (const st of r.salesTeams) {
                        if (st && st.name && !salesTeams.includes(st.name)) salesTeams.push(st.name);
                        if (st && st.company && st.company.name) companyName = companyName || st.company.name;
                                    if (Array.isArray(st.salesTeamManagers)) {
                                            for (const m of st.salesTeamManagers) {
                                                const mName = `${m.firstName || ''} ${m.lastName || ''}`.trim();
                                                if (mName && !salesTeamManagers.includes(mName)) salesTeamManagers.push(mName);
                                            }
                                        }
                    }
                }
            }
        } catch (e) { /* ignore */ }
        // also compute a statusClass to pick the icon
        const statusClass = (function(){
            const s = status || '';
            if (!s) return '';
            return s.toLowerCase().replaceAll(' ', '-');
        })();
        let statusIcon = null;
        try {
            const iconNames = ['basic','advanced-silver','advanced-gold','professional-silver','professional-gold','elite-silver','elite-gold','star-seller'];
            const match = iconNames.find(n => statusClass === n || statusClass.includes(n));
            if (match) statusIcon = `/assets/imgs/ranks/svg/${match}.svg`;
        } catch (e) {}
        // determine if this colleague is an Area Manager
        let isAreaManager = false;
        try {
            // role name may include salesTeamManager
            if (Array.isArray(colleague?.roles)) {
                for (const r of colleague.roles) {
                    if (r && r.name && String(r.name).toLowerCase().includes('salesteammanager')) {
                        isAreaManager = true;
                        break;
                    }
                }
            }
        } catch (e) {}
        // also consider them an area manager if they appear in the salesTeamManagers list
        if (!isAreaManager && salesTeamManagers.includes(name)) isAreaManager = true;

        const salesTeamManagersFiltered = (salesTeamManagers || []).filter(m => m !== name);

        return { name, status, statusClass, statusIcon, companyName, salesTeams, salesTeamManagers, salesTeamManagersFiltered, isAreaManager };
    });

    console.log('Admin Store in Nav:', adminStore);

    const { unseenCount: changelogUnseenCount } = storeToRefs(changelogStore);

    const idOwnTeam = computed(() => {
        if (!userStore.isSalesTeamManager) return null;
        return userStore.currentSelection.campaign?.roles?.find((role) => ["SMA:salesTeamManager"].includes(role.name))?.team?.id ?? null;
    });

    const exceededToDos = computed(() => {
        if (userStore.currentSelection.scope!=="salesAgent") return null;
        
        // if (userStore.lastName === 'vom Hofe') return null; // debug only
        const salesPitchFollowUps = businessPartnersStore.businessPartnerListBySignature(`${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:Wiedervorlagen`);
        const salesPitchFeedbacks = businessPartnersStore.businessPartnerListBySignature(`${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:VorOrtTermine`);
        return {
            both: (salesPitchFollowUps || []).length > userStore.maxToDos.followUp && (salesPitchFeedbacks || []).length > userStore.maxToDos.feedback,
            followUps: (salesPitchFollowUps || []).length > userStore.maxToDos.followUp,
            feedbacks: (salesPitchFeedbacks || []).length > userStore.maxToDos.feedback
        }
    });
    // Module flags for navigation gating (backend may send e.g. 'Incentive', 'Performance', 'Activity', 'Bewertungen', 'Eigenrecherche')
    const moduleFlags = computed(() => ({
        incentive: userStore.hasModule('Incentive'),
        performance: userStore.hasModule('Performance'),
        activity: userStore.hasModule('Activity'),
        ratings: userStore.hasModule('Bewertungen'),
        leadGeneration: userStore.hasModule('Eigenrecherche'),
        potential: userStore.hasModule('Potential'),
        funnelAnalysis: userStore.hasModule('FunnelAnalyse'),
        googleAdsCalculator: userStore.hasModule('GoogleAdsCalculator'),
    }));
    console.log('Module Flags in Nav:', moduleFlags.value);

    // Centralized counters (avoid duplicate getter calls and ensure reactivity clarity)
    // Neue Zähl-Logik: Trefferanzahl aus view.countBusinessPartnerFiltered über den Count-Store
    const todayFollowUpsCount = computed(() => businessPartnersCountStore.businessPartnersListsCountBySignature(`${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#Today:WiedervorlagenCount`));
    const todayAppointmentsCount = computed(() => businessPartnersCountStore.businessPartnersListsCountBySignature(`${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#Today:VorOrtTermineCount`));
    const toDoFollowUpsCount = computed(() => businessPartnersCountStore.businessPartnersListsCountBySignature(`${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:WiedervorlagenCount`));
    const toDoAppointmentsCount = computed(() => businessPartnersCountStore.businessPartnersListsCountBySignature(`${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:VorOrtTermineCount`));

    // Gesamtzähler für alle "roten" Hinweise im Burger-Menü (Today/ToDos + neue Changelogs)
    const burgerBadgeCount = computed(() => {
        const values = [
            todayFollowUpsCount.value,
            todayAppointmentsCount.value,
            toDoFollowUpsCount.value,
            toDoAppointmentsCount.value,
            changelogUnseenCount.value,
        ];
        return values.reduce((sum, v) => {
            const n = Number(v);
            return sum + (Number.isFinite(n) && n > 0 ? n : 0);
        }, 0);
    });

    // Prevent navigation when module not available
    function preventIfNoModule(moduleKey, evt) {
        if (!moduleFlags.value[moduleKey]) {
            evt.preventDefault();
            notificationStore.addNotification('Modul nicht freigeschaltet', 'error');
        } else {
            toggleBurgerMenu();
        }
    }
  
    onMounted(() => {
        updateNavigationHeight()
        document.addEventListener('click', handleClickOutside);
        window.addEventListener('resize', updateNavigationHeight)
        reflectPreference();
        ensureManagerFilterOptionsLoaded();
    });
    onUpdated(() => {
        updateNavigationHeight()
    })
    onBeforeUnmount(() => {
        document.removeEventListener('click', handleClickOutside);
    });
    onUnmounted(() => {
        window.removeEventListener('resize', updateNavigationHeight)
    })

    const handleLogOut = () => {
        localStorage.removeItem('rememberMe')
        localStorage.removeItem('email')
        localStorage.removeItem('password')
        router.push({ name: 'Login'});
    }
    
    // campaign management
    const handleChangeCampaign = async (campaign) => {
        userStore.currentSelection.campaign = campaign
        await userStore.selectCampaign(campaign.id)
        // console.log('changing campaign to ', campaign.name, 'in module', userStore.currentSelection.module)
        // if the user changes the campaign while accessing a businessPartner, the routing should return to the dashboard
        if (['BusinessPartnerList'].includes(userStore.currentSelection.module)) {
            router.push({ name: 'Dashboard'});
        }
    }

    // Toggle Menu
    const isOpenBurgerMenu = ref(false)
    const isOpenAvatarMenu = ref(false)
    const suppressAvatarAutoCloseUntil = ref(0);
    const toggleBurgerMenu = () => {
            isOpenBurgerMenu.value = !isOpenBurgerMenu.value;
            isOpenAvatarMenu.value = false;

    }
    const toggleAvatarMenu = () => {
        const willOpen = !isOpenAvatarMenu.value;
        isOpenAvatarMenu.value = willOpen;
        isOpenBurgerMenu.value = false;
        if (willOpen) {
            suppressAvatarAutoCloseUntil.value = Date.now() + 900;
            ensureManagerFilterOptionsLoaded();
        } else {
            suppressAvatarAutoCloseUntil.value = 0;
        }
    }

    const handleClickOutside = (event) => {
        const target = event.target;
        const hamElement = document.querySelector('.ham');
        const themeElement = document.querySelector('.theme-wrapper');
        const campaignElement = document.querySelector('.campaign-select');
        const avatarEl = document.querySelector('.wrapper-actions .avatar');
        const avatarMenuEl = document.querySelector('.avatar-dropdown');

        // Burger Menü schließen
        if (
            (!hamElement || !hamElement.contains(target)) &&
            (!themeElement || !themeElement.contains(target)) &&
            (!campaignElement || !campaignElement.contains(target)) &&
            isOpenBurgerMenu.value === true
        ) {
            isOpenBurgerMenu.value = false;
        }

        // Avatar Menü schließen (ignore if just opened)
        if (isOpenAvatarMenu.value === true) {
            if (Date.now() < (suppressAvatarAutoCloseUntil.value || 0)) {
                // recently opened; skip auto-close
            } else if ((!avatarEl || !avatarEl.contains(target)) && (!avatarMenuEl || !avatarMenuEl.contains(target))) {
                isOpenAvatarMenu.value = false;
            }
        }
    };

    const incentiveInformations = computed(() => {
        // only get the information on a salesAgent not on management
        if (userStore.isManagement || userStore.isSalesTeamManager || userStore.isWinback) {
            return {
                bonus: 0,
                entitled: false,
                name: `${userStore.firstName} ${userStore.lastName}`,
            }
        }
        
        const championsExists = incentivesStore.incentive?.salesAgents?.champions?.find((champion) => champion.highlightedPosition === true);
        if (!championsExists) {
            return {
                bonus: 0,
                entitled: false,
                name: `${userStore.firstName} ${userStore.lastName}`,
            }
        } else {
            return {
                bonus: championsExists.salesBonus,
                entitled: championsExists.salesBonusEntitled,
                name: championsExists.salesAgentName,
            }
        }
    });

    function formattedSalesAgentName(name) {
    return name
        .toLowerCase() // Konvertiere in Kleinbuchstaben
        .replace(/ä/g, 'ae') // Ersetze ä durch ae
        .replace(/ö/g, 'oe') // Ersetze ö durch oe
        .replace(/ü/g, 'ue') // Ersetze ü durch ue
        .replace(/ß/g, 'ss') // Ersetze ß durch ss
        .replace(/\s+/g, '-') // Ersetze Leerzeichen durch -
        + '.webp'; // Füge die Dateiendung hinzu
    };

    const dropdownVisible = ref(false);

    const toggleDropdown = () => {
        dropdownVisible.value = !dropdownVisible.value;
    };

    const navigationRef = ref(null)
    function updateNavigationHeight() {
        if (navigationRef.value) {
            functionsStore.setNavigationHeight(navigationRef.value.getBoundingClientRect().height)
        }
    }

    const handleCampaignClick = (campaign) => {
        // Do not toggle the burger menu when selecting a campaign
        // Keep the menu open and just change the campaign
        dropdownVisible.value = false;
        handleChangeCampaign(campaign);
    };

    async function handleSelectSalesAgent(idSelection) {
        if (idSelection == null) return;
        const value = String(idSelection);
        if (value === '-1') {
            await userStore.selectCompany();
            return;
        }
        if (value.startsWith('salesAgent:')) {
            await userStore.selectSalesAgent(value.replace('salesAgent:', ''));
            return;
        }
        if (value.startsWith('salesTeam:')) {
            await userStore.selectSalesTeam(value.replace('salesTeam:', ''));
        }
    }

    const selectedSalesAgentId = ref(-1);
    async function handleLogin() {
        if (selectedSalesAgentId.value === -1) {
            notificationStore.addNotification('Bitte einen Sales Manager auswählen!', 'error');
            return;
        }
        const colleague = adminStore.colleagues.find(c => c.id === selectedSalesAgentId.value);
        if (colleague && colleague.emailAddress) {
            await adminStore.loginAsUser(colleague);
            console.log('Logging in as:', colleague.emailAddress);
        }
    }

    import UserDropdown from '@/components/UserDropdown.vue'
    import InlineSpinner from '@/components/InlineSpinner.vue'
    import userIcon from '@/assets/imgs/icons/user-solid.svg'
    import CampaignDropdown from '@/components/CampaignDropdown.vue'
    const selectedManagerScope = ref(null);
    const canSwitchSalesManagerScope = computed(() => userStore.isManagement || userStore.isSalesTeamManager || userStore.isWinback);
    const selectedSalesManagerBadgeId = computed(() => {
        if (!canSwitchSalesManagerScope.value) return null;
        if (userStore.currentSelection.scope !== 'salesAgent') return null;
        const id = Number(userStore.currentSelection.salesAgent?.id);
        return Number.isFinite(id) && id > 0 ? id : null;
    });
    const selectedSalesManagerName = computed(() => {
        if (!selectedSalesManagerBadgeId.value) return null;
        const agent = userStore.currentSelection.salesAgent;
        if (!agent) return null;
        const full = [agent.firstName, agent.lastName].filter(Boolean).join(' ').trim();
        return full || agent.name || null;
    });
    const colleaguesBySalesAgentId = computed(() => {
        const map = new Map();
        const all = [...(userStore.colleagues || []), ...(adminStore.colleagues || [])];
        for (const colleague of all) {
            const salesAgentId = colleague?.isSalesAgent?.id;
            if (!salesAgentId || map.has(Number(salesAgentId))) continue;
            map.set(Number(salesAgentId), colleague);
        }
        return map;
    });
    const managerSelectionOptions = computed(() => {
        const options = [];
        const filterOption = userStore.filterOption;
        if (!filterOption) return options;

        const allSalesAgents = (filterOption.salesAgents || [])
            .filter((agent) => agent && agent.id !== -1)
            .sort((a, b) => `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`));

        if (userStore.isManagement) {
            options.push({ id: 'heading-sales-office', _type: 'heading', label: 'Sales Office' });
            options.push({ id: '-1', name: 'Gesamter Standort' });
            const salesTeams = (filterOption.salesTeams || [])
                .filter((team) => team && team.id !== -1)
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((team) => ({ id: `salesTeam:${team.id}`, name: team.name }));
            options.push(...salesTeams);
        }

        if (userStore.isSalesTeamManager && idOwnTeam.value != null) {
            // Use the real team name from filterOptions instead of a hardcoded label
            const ownTeam = (filterOption.salesTeams || []).find((t) => t.id === idOwnTeam.value);
            const ownTeamName = ownTeam?.name || 'Meine Area';
            options.push({ id: 'heading-my-area', _type: 'heading', label: ownTeamName });
            options.push({
                id: `salesTeam:${idOwnTeam.value}`,
                firstName: 'Gesamte Area',
                lastName: '',
                name: 'Gesamte Area',
                emailAddress: null,
                hasAvatar: false,
            });
            const ownAreaAgents = allSalesAgents
                .filter((agent) => agent.teamId === idOwnTeam.value)
                .map((agent) => {
                    const colleague = colleaguesBySalesAgentId.value.get(Number(agent.id));
                    return {
                        id: `salesAgent:${agent.id}`,
                        salesAgentId: agent.id,
                        firstName: agent.firstName,
                        lastName: agent.lastName,
                        emailAddress: agent.emailAddress,
                        userId: colleague?.id || agent.idUser || null,
                        photo: colleague?.photo || agent.photo,
                        hasAvatar: Boolean(colleague?.photo || agent.photo),
                    };
                });
            options.push(...ownAreaAgents);
        }

        options.push({ id: 'heading-sales-manager', _type: 'heading', label: 'Sales Manager' });
        const salesAgents = allSalesAgents
            .map((agent) => {
                const colleague = colleaguesBySalesAgentId.value.get(Number(agent.id));
                return {
                    id: `salesAgent:${agent.id}`,
                    salesAgentId: agent.id,
                    firstName: agent.firstName,
                    lastName: agent.lastName,
                    emailAddress: agent.emailAddress,
                    userId: colleague?.id || agent.idUser || null,
                    photo: colleague?.photo || agent.photo,
                    hasAvatar: Boolean(colleague?.photo || agent.photo),
                };
            });
        options.push(...salesAgents);
        return options;
    });

    const currentScopeSelectionValue = computed(() => {
        if (userStore.currentSelection.scope === 'campaign') return '-1';
        if (userStore.currentSelection.scope === 'salesTeam') return `salesTeam:${userStore.currentSelection.salesTeam?.id}`;
        if (userStore.currentSelection.scope === 'salesAgent') return `salesAgent:${userStore.currentSelection.salesAgent?.id}`;
        return null;
    });

    watch([managerSelectionOptions, currentScopeSelectionValue], ([options, currentValue]) => {
        if (!currentValue) {
            selectedManagerScope.value = null;
            return;
        }
        selectedManagerScope.value = options.find((option) => option && option._type !== 'heading' && String(option.id) === String(currentValue)) || null;
    }, { immediate: true });

    async function ensureManagerFilterOptionsLoaded() {
        if (!canSwitchSalesManagerScope.value) return;
        if (userStore.filterOption?.salesAgents?.length || userStore.filterOption?.salesTeams?.length) return;
        if (userStore.filterOptions.isLoading === true) return;
        try {
            await userStore.refreshFilterOptions({ resetFilters: false });
        } catch (error) {
            console.error('Filteroptionen für Avatar-Menü konnten nicht geladen werden', error);
        }
    }

    function onSelectColleague(opt){
        if (!opt || opt._type === 'heading') return;
        selectedManagerScope.value = opt;
        handleSelectSalesAgent(opt.id);
    }

</script>

<template>
    <div class="loadingWrapper">
        <!-- Ladebalken für Kampagnen-Ladezeit nur auf Dashboard anzeigen und während selectCampaign läuft -->
        <ProgressBar v-if="route.name === 'Dashboard' && userStore._isSelectingCampaign" :percent="campaignLoadingStore.percent" :visible="true" />
        <!-- <div class="campaignLoader" v-if="userStore.currentSelection.campaign.isLoading">Kampagne lädt...<span class="loader"></span></div> -->
        <header id="mainHeader">
            <nav class="max-width" ref="navigationRef">
                <!-- Navigation Height: {{ functionsStore.navigationHeight }} -->
                <div class="wrapper-logo">
                    <router-link to="/dashboard"><img width="64" height="64" src="@/assets/imgs/logos/sellwerk/sellwerk-favicon.svg" alt="Sellwerk Favicon"></router-link>
                    <!-- <p>sma 2.0</p> -->
                </div>
                <div v-if="adminStore.isAdmin && userStore.database" class="admin-db-warning">
                    <span class="warning-icon" aria-hidden="true">&#9888;</span>
                    <span class="db-info-text">
                        <strong>{{ userStore.database?.name }}</strong>
                        <span v-if="userStore.database?.description">{{ userStore.database?.description }}</span>
                    </span>
                </div>
                <div class="wrapper-actions">
                    <!-- <div v-if="userStore.loginStatus==='success'" class="wrapper-campaign">
                        <div v-for="publication of userStore.currentSelection.campaign?.publicationList || []" :key="publication.id" class="wrapper-publication-single">
                            <img v-if="publication.startsWith('GS')" src="@/assets/imgs/logos/campaigns/Gelbe-Seiten-App-Icon.svg" alt="Logo Gelbe Seiten">
                            <img v-if="publication.startsWith('TB')" src="@/assets/imgs/logos/campaigns/Das-Telefonbuch-App-Icon.svg" alt="Logo Telefonbuch">
                            <img v-if="publication.startsWith('OE')" src="@/assets/imgs/logos/campaigns/Das-Oertliche-App-Icon.svg" alt="Logo Das Örtliche">
                            <span>{{ publication.split(' ')[1] }}</span>
                        </div>
                    </div> -->
                    <!-- <span class="divider"></span> -->
                    <!-- <svg class="ham hamRotate ham8" viewBox="0 0 100 100" width="64" onclick="this.classList.toggle('active')"> -->
                    <div class="avatar-wrapper" v-if="userStore.loginStatus==='success' && userStore.preferences.privacy.showAvatar">
                        <userAvatar class="avatar clickable" size="36" :isUserAvatar="true" :idUser="userStore.id" :hide-text="true" :no-margin="true" :no-padding="true" @click="toggleAvatarMenu()"/>
                        <userAvatar
                            v-if="selectedSalesManagerBadgeId"
                            class="avatar-selected-badge"
                            :size="22"
                            :isSalesAgent="true"
                            :salesAgentId="selectedSalesManagerBadgeId"
                            :hide-text="true"
                            :no-padding="true"
                            :no-margin="true"
                        />
                        <transition name="fade">
                            <div v-show="isOpenAvatarMenu" class="avatar-dropdown">
                                <div class="user-block-meta" v-if="isOpenAvatarMenu">
                                    <div class="user-block-inner">
                                        <userAvatar :size="56" :isUserAvatar="true" :idUser="userStore.id" :hide-text="true" :no-padding="true" :no-margin="true" />
                                        <div class="avatar-meta-details">
                                            <div class="avatar-meta-name">{{ avatarMenuInfo.name }}</div>
                                            <div v-if="avatarMenuInfo.isAreaManager" class="avatar-meta-status">Area Manager</div>
                                            <div v-else class="avatar-meta-status">{{ avatarMenuInfo.status || 'Kein Status' }}</div>
                                            <div v-if="avatarMenuInfo.companyName" class="avatar-meta-line"><strong>Standort:</strong> {{ avatarMenuInfo.companyName }}</div>
                                            <div v-if="avatarMenuInfo.salesTeams && avatarMenuInfo.salesTeams.length" class="avatar-meta-line"><strong>Team:</strong> {{ avatarMenuInfo.salesTeams.join(', ') }}</div>
                                            <div v-if="avatarMenuInfo.salesTeamManagers && avatarMenuInfo.salesTeamManagers.length && (avatarMenuInfo.salesTeamManagersFiltered && avatarMenuInfo.salesTeamManagersFiltered.length>0)" class="avatar-meta-line"><strong>Area Manager:</strong> {{ avatarMenuInfo.salesTeamManagersFiltered.join(', ') }}</div>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="canSwitchSalesManagerScope && (userStore.filterOptions.isLoading || managerSelectionOptions.length === 0)" class="avatar-switcher avatar-switcher--loading">
                                    <InlineSpinner size="sm" />
                                    <span class="avatar-switcher-loading-label">Auswahl wird geladen…</span>
                                </div>
                                <div v-else-if="canSwitchSalesManagerScope && managerSelectionOptions.length > 0" class="avatar-switcher">
                                    <UserDropdown
                                        v-model="selectedManagerScope"
                                        :options="managerSelectionOptions"
                                        :option-key="'id'"
                                        :option-label="(o)=> o ? ((o.firstName && o.lastName) ? (o.firstName + ' ' + o.lastName) : (o.name || o.label || '')) : ''"
                                        :search-prop="['firstName','lastName','name','emailAddress']"
                                        :show-avatar="true"
                                        :show-avatar-in-trigger="true"
                                        :show-avatar-in-list="true"
                                        :avatar-size="24"
                                        trigger-label="Ansicht wechseln"
                                        placeholder="Auswahl treffen"
                                        :trigger-icon-src="userIcon"
                                        :showId="false"
                                        :disable-sort="true"
                                        @select="onSelectColleague"
                                    />
                                </div>
                                <ul class="menu-list">
                                    <li class="spacer-item" aria-hidden="true"></li>
                                    <li>
                                        <button class="link-btn with-icon logout-btn" @click="handleLogOut(); isOpenAvatarMenu=false">
                                            <img src="@/assets/imgs/icons/arrow-right-to-bracket-solid.svg" alt="" aria-hidden="true" />
                                            <span>Abmelden</span>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </transition>
                    </div>
                    <div class="burger-wrapper">
                        <svg :class="['ham hamRotate ham8', {'active' : isOpenBurgerMenu}]" viewBox="0 0 100 100" width="64" @click="toggleBurgerMenu()">
                            <path class="line top" d="m 30,33 h 40 c 3.722839,0 7.5,3.126468 7.5,8.578427 0,5.451959 -2.727029,8.421573 -7.5,8.421573 h -20" />
                            <path class="line middle" d="m 30,50 h 40" />
                            <path class="line bottom" d="m 70,67 h -40 c 0,0 -7.5,-0.802118 -7.5,-8.365747 0,-7.563629 7.5,-8.634253 7.5,-8.634253 h 20" />
                        </svg>
                        <span v-if="burgerBadgeCount > 0" class="number-label burger-badge">{{ burgerBadgeCount }}</span>
                    </div>
                </div>
            </nav>

            <nav :class="['menu-content burgerMenu', {'active' : isOpenBurgerMenu}]">
                <div v-if="userStore.loginStatus==='success'" class="nav-content-wrapper">
                    <userAvatar v-if="userStore.preferences.privacy.showAvatar" layout="row" :isUserAvatar="true" :idUser="userStore.id" />
                    <hr v-if="userStore.campaigns.filter((campaign) => campaign.id !==userStore.currentSelection.campaign?.id ).length>0">
                    <!-- <div v-if="userStore.campaigns?.length>1" class="campaign-select">
                        <CampaignDropdown
                          :options="userStore.campaigns.filter(c => c.id !== userStore.currentSelection.campaign?.id)"
                          trigger-label="Kampagne wechseln"
                          placeholder="Auswahl treffen"
                          @select="handleCampaignClick"
                        />
                    </div> -->
                    <!-- <hr> -->
                    <ul :class="['main-nav', (userStore.campaigns.filter((campaign) => campaign.id !==userStore.currentSelection.campaign?.id ).length === 0) ? 'no-campaign-select' : '']">
                        <li><router-link @click="toggleBurgerMenu" to="/dashboard">Dashboard</router-link>
                            <ul>
                                <li v-if="moduleFlags.incentive">
                                    <router-link
                                        :class="['deactivated', { 'deactivated': !moduleFlags.incentive }]"
                                        :aria-disabled="!moduleFlags.incentive"
                                        @click="preventIfNoModule('incentive', $event)"
                                        to="incentives">Champions</router-link>
                                </li>
                                <li><router-link :class="[{'deactivated' : (exceededToDos && (exceededToDos.feedbacks===true || exceededToDos.followUps===true))}]" @click="toggleBurgerMenu" to="businessPartnerList?filter=id:-1|idSelection:-1|sort:businessPartner.name,asc&page=0">Potential & BK</router-link>
                                    <ul>
                                        <li><router-link :class="[{ 'deactivated' : todayFollowUpsCount === 0 }]" :aria-disabled="todayFollowUpsCount===0" data-count="today-followups" @click="toggleBurgerMenu" to="/salesPitchFollowUp">Wiedervorlagen Heute <span class="number-label xs">{{ todayFollowUpsCount }}</span></router-link></li>
                                        <li><router-link :class="[{ 'deactivated' : todayAppointmentsCount === 0 }]" :aria-disabled="todayAppointmentsCount===0" data-count="today-appointments" @click="toggleBurgerMenu" to="/salesPitchFeedback">Termine Heute <span class="number-label xs">{{ todayAppointmentsCount }}</span></router-link></li>
                                        
                                        <li><router-link :class="[{ 'deactivated' : toDoFollowUpsCount === 0 }]" :aria-disabled="toDoFollowUpsCount===0" data-count="todo-followups" @click="toggleBurgerMenu" to="/salesPitchFollowUp">Wiedervorlagen To-Do <span class="number-label xs">{{ toDoFollowUpsCount }}</span></router-link></li>
                                        <li><router-link :class="[{ 'deactivated' : toDoAppointmentsCount === 0 }]" :aria-disabled="toDoAppointmentsCount===0" data-count="todo-appointments" @click="toggleBurgerMenu" to="/salesPitchFeedback">Termin-Feedback <span class="number-label xs">{{ toDoAppointmentsCount }}</span></router-link></li>
                                    </ul>
                                </li>
                                <li v-if="moduleFlags.activity">
                                    <router-link
                                        :class="['deactivated', { 'deactivated': !moduleFlags.activity }]"
                                        :aria-disabled="!moduleFlags.activity"
                                        @click="preventIfNoModule('activity', $event)"
                                        to="activity">Aktivitäten</router-link>
                                </li>
                                <li v-if="moduleFlags.performance">
                                    <router-link
                                        :class="['deactivated', { 'deactivated': !moduleFlags.performance }]"
                                        :aria-disabled="!moduleFlags.performance"
                                        @click="preventIfNoModule('performance', $event)"
                                        to="performance">Performance</router-link>
                                </li>
                                <li v-if="moduleFlags.ratings">
                                    <router-link
                                        :class="['deactivated', { 'deactivated': !moduleFlags.ratings }]"
                                        :aria-disabled="!moduleFlags.ratings"
                                        @click="preventIfNoModule('ratings', $event)"
                                        to="reviews">Bewertungen</router-link>
                                </li>
                                <li v-if="moduleFlags.funnelAnalysis && (userStore.isManagement || userStore.isSalesTeamManager || userStore.isWinback)">
                                    <router-link @click="toggleBurgerMenu" to="funnelAnalysis">Funnel Analyse</router-link>
                                </li>
                                <li>
                                    <router-link @click="toggleBurgerMenu" to="/changelog" class="changelog-link">
                                        <span class="changelog-link-icon">
                                            <img src="@/assets/imgs/icons/book-solid.svg" alt="" aria-hidden="true" />
                                        </span>
                                        <span class="changelog-link-label">Changelog</span>
                                        <span v-if="changelogUnseenCount > 0" class="number-label xs">{{ changelogUnseenCount }}</span>
                                    </router-link>
                                </li>
                            </ul>
                        </li>
                        <li v-if="moduleFlags.googleAdsCalculator"><router-link class="deactivated" @click="toggleBurgerMenu" to="reviews">Google Ads Kostenrechner</router-link></li>
                    </ul>
                </div>
                <!-- <button class="link-btn with-icon danger-logout" @click="handleLogOut()">
                    <img src="@/assets/imgs/icons/arrow-right-from-bracket-solid.svg" alt="Logout" aria-hidden="true" />
                    <span>Logout</span>
                </button> -->
            </nav>
            <div class="notifications-wrapper">
                <notification v-for="notification in notifications" :key="notification.id" :notification="notification" @hideNotification="removeNotification(notification.id)" />
            </div>
        </header>
        <div v-if="!adminStore.loginAs && adminStore.colleagues.length > 0" class="login-wrapper">
            <div :class="['form-wrapper', {'open' : true}]"> 
                <div v-if="userStore.isLoading === true" class="loader-overlay">
                    <div class="loader" id="loader"></div>
                    <div class="loadingDescription">Du wirst eingeloggt</div>
                </div>
                <div v-if="true" id="form" :class="['wrapper-right', {'error' : userStore.loginStatus === 'error'}]">
                    <div class="screen-content">
                        <form class="login" @submit.prevent="submit">
                            <div class="form-header">
                                <div    data-aos="fade-left"
                                        data-aos-easing="ease-out-sine"
                                        data-aos-delay="1250"
                                        data-aos-anchor="#form"
                                class="headline">
                                    <sup>SMA 26.3.3</sup>
                                </div>
                                <!-- <p class="subline">Bitte logge dich ein.</p> -->
                            </div>
                            <p v-if="userStore.loginStatus === 'error'" class="error-msg">Es ist ein Fehler aufgetreten. Bitte überprüfe Deine Eingaben und versuche es erneut.</p>
                            <div class="headline-txt">
                                <p  data-aos="fade-right"
                                    data-aos-easing="ease-out-sine"
                                    data-aos-delay="100"
                                    data-aos-anchor="#form"
                                class="headline">Wähle einen Sales Manager aus!.</p>
                                <p  data-aos="fade-right"
                                    data-aos-easing="ease-out-sine"
                                    data-aos-delay="200"
                                    data-aos-anchor="#form"
                                class="subline">Nutze alle wichtigen Daten auf einen Blick. <br> Analysiere seine Umsätze, Verkäufe und Zahlen.</p>
                            </div>
                            <div class="input-wrapper">
                                <!-- Replaced native select with custom UserDropdown -->
                                <!-- Auswahl triggert nur Setzen der ID; Login erfolgt erst per Button-Klick -->
                                <UserDropdown
                                    v-model="selectedSalesAgentId"
                                    :options="adminStore.colleagues"
                                    :option-key="'id'"
                                    :option-label="(o)=> o ? ((o.firstName && o.lastName) ? (o.firstName + ' ' + o.lastName) : (o.name || '')) : ''"
                                    :search-prop="['firstName','lastName','emailAddress']"
                                    :show-avatar="true"
                                    :show-avatar-in-trigger="true"
                                    :show-avatar-in-list="false"
                                    :avatar-size="28"
                                    trigger-label="User auswählen"
                                    placeholder="Auswahl treffen"
                                    :trigger-icon-src="userIcon"
                                    :showId="true"
                                />
                            </div>
                            <button     data-aos="fade-up"
                                        data-aos-easing="ease-out-sine"
                                        data-aos-delay="400"
                                        data-aos-anchor="#form"
                                        @click="handleLogin()"
                                        :disabled="selectedSalesAgentId === -1 || selectedSalesAgentId === null"
                                        class="btn btn--auth btn--sm btn--pill login-submit">
                                <img alt="Einloggen" src="@/assets/imgs/icons/arrow-right-to-bracket-solid.svg" />
                                <span>Einloggen</span>
                            </button>
                        </form>
                    </div>
                    <div class="screen-background">
                        <span   data-aos="rotate"
                                data-aos-easing="ease-out-sine"
                                data-aos-delay="60"
                                data-aos-anchor="#form"
                                class="screen-background-shape screen-background-shape4"></span>
                        <span   data-aos="rotate"
                                data-aos-easing="ease-out-sine"
                                data-aos-delay="80"
                                data-aos-anchor="#form"
                                class="screen-background-shape screen-background-shape3"></span>		
                        <span   data-aos="rotate"
                                data-aos-easing="ease-out-sine"
                                data-aos-delay="70"
                                data-aos-anchor="#form"
                                class="screen-background-shape screen-background-shape2"></span>
                        <span   data-aos="rotate"
                                data-aos-easing="ease-out-sine"
                                data-aos-delay="50"
                                data-aos-anchor="#form"
                                class="screen-background-shape screen-background-shape1"></span>
                    </div>		
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.avatar-wrapper { position:relative; }
.avatar-wrapper .sm-wrapper {
    margin-top: 0;
}
.avatar-selected-badge {
    position: absolute;
    right: -4px;
    bottom: -4px;
    z-index: 2;
    border-radius: 50%;
    pointer-events: none;
}

/* Ensure badge element inside child component is positioned correctly even with scoped styles */
::v-deep .avatar-wrapper .avatar-selected-badge {
    position: absolute !important;
    right: -6px !important;
    bottom: -6px !important;
    z-index: 30 !important;
    pointer-events: none !important;
}

/* Remove unintended top margin for sales-manager avatar instances */
::v-deep .sm-wrapper.nav.layout-row.no-padding {
    margin-top: 0 !important;
}

.selected-agent-badge {
    display: flex;
    align-items: center;
    gap: .35rem;
    /* margin-top: .35rem; */
    padding: .25rem .5rem;
    background: rgba(255,255,255,.07);
    border-radius: 8px;
    font-size: .6rem;
    line-height: 1;
}
.theme--light .selected-agent-badge {
    background: rgba(0,0,0,.06);
}
.selected-agent-label {
    opacity: .6;
    font-weight: 500;
}
.selected-agent-name {
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
}
.burger-wrapper { position:relative; display:inline-block; width: 64px; height: 64px;}
.burger-badge {
    position:absolute;
    top:-4px;
    right:-4px;
}
/* Sicherstellen, dass das Burger-Icon durch den Wrapper nicht skaliert wird */
.burger-wrapper .ham {
    width: 64px;
    height: 64px;
    flex-shrink: 0;
}
/* Login button alignment (new button system) */
button.login-submit { width:auto; gap:.45rem; margin:1rem 0 0 0; justify-content:center; text-align:center; }
button.login-submit img { margin:0; }
@media (max-width: 720px) { button.login-submit { margin:1.1rem 0 0 0; } }
/* Dropdown repositioned: use CSS var for nav height if present */
.avatar-dropdown { position:absolute; right:0; top:calc(100% + 10px); background:rgba(20,23,28,0.97); border:1px solid #2c3138; padding:1.05rem .95rem .95rem; border-radius:16px; width:360px; max-width:calc(100vw - 1rem); /* simplified shadow for better performance */ box-shadow:0 14px 32px -16px rgba(0,0,0,.45); /* remove heavy backdrop-filter */ z-index:220; animation:dropIn .18s ease; }
.avatar-dropdown:before { content:""; position:absolute; top:-9px; right:18px; width:18px; height:18px; background:inherit; border:1px solid #2c3138; border-bottom:none; border-right:none; transform:rotate(45deg); }
.theme--light .avatar-dropdown { background:rgba(255,255,255,0.92); border-color:#d9e2ec; box-shadow:0 18px 40px -18px rgba(30,41,59,.25),0 6px 20px -10px rgba(30,41,59,.18); }
.theme--light .avatar-dropdown:before { border-color:#d9e2ec; background:inherit; }
.avatar-dropdown .user-block { font-size:.72rem; line-height:1.25; margin:0 0 .75rem; font-weight:600; letter-spacing:.5px; }
.avatar-dropdown .email { opacity:.55; word-break:break-all; font-size:.58rem; margin-top:.2rem; font-weight:500; }
.avatar-dropdown .menu-list { list-style:none; padding:0; margin:.35rem 0 0; display:flex; flex-direction:column; gap:.2rem; }
.avatar-dropdown .user-block-meta { padding-bottom: .35rem; margin-bottom: .45rem; border-bottom: 1px solid rgba(255,255,255,0.03); }
.avatar-dropdown .user-block-meta .user-block-inner { display:flex; gap:.5rem; align-items:flex-start; }
.avatar-dropdown .avatar-meta-details { min-width:0; }
.avatar-dropdown .avatar-meta-name { font-weight:800; font-size:1rem; color:var(--text, #fff); }
.avatar-dropdown .avatar-meta-status { font-size:0.88rem; color:var(--muted, rgba(255,255,255,0.75)); margin-top:0; }
.avatar-dropdown .avatar-meta-line { font-size:0.84rem; color:var(--muted, rgba(255,255,255,0.72)); }
.avatar-dropdown .menu-list .spacer-item { height:4px; }
.avatar-dropdown .link-btn.with-icon { display:flex; align-items:center; gap:.6rem; padding:.5rem .6rem; border-radius:10px; background:transparent; border:none; color:inherit; cursor:pointer; transition:background .12s ease; }
.avatar-dropdown .link-btn.with-icon img { width:18px; height:18px; }
.avatar-dropdown .link-btn.with-icon:hover { background:rgba(255,255,255,0.02); }
.avatar-dropdown .logout-btn { display: flex; align-items: center; justify-content: center; margin-top:6px; width:100%; justify-content:center; font-weight:800; color:#fff; border:1px solid rgba(239,68,68,0.18); padding:.72rem .75rem; box-shadow:0 8px 20px -8px rgba(239,68,68,0.28); }
.avatar-dropdown .logout-btn img { filter: brightness(0) invert(1); }
.avatar-switcher { margin: .25rem 0 .55rem; }
.avatar-switcher--loading { display: flex; align-items: center; gap: .45rem; padding: .35rem .1rem; opacity: .75; }
.avatar-switcher-loading-label { font-size: .62rem; color: inherit; }
.avatar-dropdown a, .avatar-dropdown .link-btn { display:block; width:100%; text-align:left; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.05); color:#d4d9e0; font:inherit; padding:.55rem .6rem; border-radius:10px; cursor:pointer; font-size:.66rem; text-decoration:none; letter-spacing:.45px; transition:.18s; }
.avatar-dropdown a:hover, .avatar-dropdown .link-btn:hover { background:rgba(255,255,255,0.03); border-color:rgba(255,255,255,0.1); color:#fff; }
.avatar-dropdown a.with-icon, .avatar-dropdown .link-btn.with-icon { display:flex; align-items:center; gap:.55rem; }
/* Icon refinement: remove invert (was causing broken look), ensure crisp scaling */
.avatar-dropdown a.with-icon img, .avatar-dropdown .link-btn.with-icon img {
    width:.95rem; height:.95rem; flex-shrink:0; display:block;
    /* If original SVG is monochrome dark, we softly brighten for dark bg */
    filter:brightness(1.15);
    opacity:.88;
}
.avatar-menu-status-icon { width:18px; height:18px; vertical-align:middle; }

.avatar-meta-details { display:flex; flex-direction:column; gap:0px; }
.avatar-meta-name { font-weight:800; font-size:0.98rem; color:#0f172a; }
.avatar-meta-status { font-size:0.86rem; color:#64748b; }
.avatar-meta-line { font-size:0.82rem; color:#64748b; margin-top: 0 }
/* Light theme: neutralize brightness so original design stays intact */
.theme--light .avatar-dropdown a.with-icon img,
.theme--light .avatar-dropdown .link-btn.with-icon img {
    filter:none; opacity:.8;
}
.theme--light .avatar-dropdown a, .theme--light .avatar-dropdown .link-btn { color:#1e293b; background:#f8fafc; border-color:#dfe6ee; }
.theme--light .avatar-dropdown a:hover, .theme--light .avatar-dropdown .link-btn:hover { background:#f8fafc; border-color:#dfe6ee; color:#1e293b; }
/* Focus state (keyboard): keep accessibility high without bright hover flash */
.avatar-dropdown a:focus-visible, .avatar-dropdown .link-btn:focus-visible { outline:2px solid #3b82f6; outline-offset:2px; }
.theme--light .avatar-dropdown a:focus-visible, .theme--light .avatar-dropdown .link-btn:focus-visible { outline:2px solid #2563eb; }
@keyframes dropIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease, transform .15s ease; }
.fade-enter-from, .fade-leave-to { opacity:0; transform:translateY(-4px); }
/* Scoped white login button style - only when inside a .login-wrapper context to avoid affecting admin selection */
.login-wrapper button.login-submit {
  background:#fff;
  color:#111827;
  border:1px solid #e2e8f0;
  box-shadow:0 4px 14px -6px rgba(0,0,0,.18),0 2px 6px -2px rgba(0,0,0,.12);
  font-weight:600;
  letter-spacing:.4px;
}
.login-wrapper button.login-submit:hover { background:#f8fafc; }
.login-wrapper button.login-submit:active { background:#eef2f6; box-shadow:0 2px 8px -4px rgba(0,0,0,.25) inset; }
.login-wrapper button.login-submit:disabled { opacity:.5; cursor:not-allowed; background:#fff; box-shadow:none; }
.login-wrapper button.login-submit > img { filter:none; opacity:.85; }
.theme--dark .login-wrapper button.login-submit { background:#fff; color:#0f172a; }

/* Changelog link in burger navigation */
.main-nav .changelog-link {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
}

.main-nav .changelog-link-icon img {
    width: 1.05rem;
    height: 1.05rem;
    display: block;
    filter: var(--color-sellwerk-grade-two-filter);
}

.menu-content.burgerMenu .link-btn.danger-logout {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: #fff;
    color: #f44336;
    border: 1.5px solid #f44336;
    border-radius: 8px;
    font-weight: 600;
    box-shadow: none;
    transition: background 0.18s, color 0.18s, border 0.18s;
}

.menu-content.burgerMenu .link-btn.danger-logout:hover,
.menu-content.burgerMenu .link-btn.danger-logout:focus-visible {
    background: rgba(244, 67, 54, 0.08);
    color: #d7352a;
    border-color: #d7352a;
}

.menu-content.burgerMenu .link-btn.danger-logout:active {
    background: rgba(244, 67, 54, 0.16);
    color: #bf2f24;
    border-color: #bf2f24;
}

.menu-content.burgerMenu .link-btn.danger-logout img {
    opacity: 0.9;
    filter: none;
}

/* Moderner Warnhinweis für Admins (Datenbank-Info)
   min-width:180px war ein harter Sockel, der zusammen mit Logo + Avatar +
   Burger-Menü in der fixen 75px-Nav-Zeile (header.scss) auf schmalen
   Bildschirmen nicht mehr passte — der Text brach auf mehrere Zeilen um, die
   die fixe Zeilenhöhe nicht vorsah, und kollidierte sichtbar mit den
   Geschwister-Elementen. Jetzt schrumpft die Box mit und ihr Text kürzt sich
   per Ellipsis ab, statt umzubrechen. */
.admin-db-warning {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    background: linear-gradient(90deg, #fffbe6 0%, #ffe6e6 100%);
    color: #b45309;
    border: 1.5px solid #fbbf24;
    border-radius: 8px;
    padding: 0.45rem 1.1rem 0.45rem 0.85rem;
    font-size: 0.98rem;
    font-weight: 500;
    margin: 0.5rem 0 0.5rem 0.5rem;
    box-shadow: 0 2px 8px -4px #fbbf2433;
    max-width: 420px;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    z-index: 10;
    animation: fadeInWarn 0.5s;
}
@media (min-width: 600px) {
    .admin-db-warning {
        min-width: 180px;
    }
}
.admin-db-warning .warning-icon {
    font-size: 1.25rem;
    color: #d97706;
    flex-shrink: 0;
    filter: drop-shadow(0 1px 2px #fbbf2466);
}
.admin-db-warning .db-info-text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    min-width: 0;
}
.admin-db-warning .db-info-text strong,
.admin-db-warning .db-info-text span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.admin-db-warning strong {
    font-weight: 700;
    letter-spacing: 0.02em;
}
@keyframes fadeInWarn {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
}

</style>